import React from 'react';
import { Box, Typography, TextField } from '@weave-mui/material';
import Checkbox from '@weave-mui/checkbox';
import Switch from '@weave-mui/switch';

/**
 * NotificationSection - Reusable component for test report notification settings
 *
 * Handles "Notify me" and "Notify others" checkboxes with email list management.
 * Automatically adds/removes user email when "Notify me" is toggled.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.notifyMe - Whether to notify current user
 * @param {boolean} props.notifyOthers - Whether to notify other users
 * @param {string} props.notificationEmail - Email addresses for notifications
 * @param {string} props.userEmail - Current user's email address
 * @param {Function} props.onNotifyMeChange - Callback when "Notify me" checkbox changes
 * @param {Function} props.onNotifyOthersChange - Callback when "Notify others" checkbox changes
 * @param {Function} props.onNotificationEmailChange - Callback when email field changes
 * @param {boolean} [props.showJiraSection=false] - Whether to show JIRA section
 * @param {boolean} [props.updateJiraXray=false] - Whether to update JIRA Xray
 * @param {Function} [props.onJiraXrayChange] - Callback when JIRA Xray toggle changes
 * @param {boolean} [props.renderEmailField=true] - Whether to render the email field (set to false if parent handles it, e.g., for Formik validation)
 * @returns {JSX.Element} The notification section component
 */
function NotificationSection({
  notifyMe,
  notifyOthers,
  notificationEmail,
  userEmail,
  onNotifyMeChange,
  onNotifyOthersChange,
  onNotificationEmailChange,
  showJiraSection = false,
  updateJiraXray = false,
  onJiraXrayChange,
  renderEmailField = true,
}) {
  /**
   * Handle notify me checkbox change
   * Automatically adds/removes user email from the email list
   */
  const handleNotifyMeChange = (e) => {
    const checked = e.target.checked;
    const trimmedUserEmail = userEmail?.trim() || '';

    if (!trimmedUserEmail) {
      onNotifyMeChange(checked);
      return;
    }

    let emailList = (notificationEmail || '')
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);

    if (checked) {
      // Add user email if not present
      if (!emailList.includes(trimmedUserEmail)) {
        emailList = [trimmedUserEmail, ...emailList];
      }
    } else {
      // Remove user email
      emailList = emailList.filter((e) => e !== trimmedUserEmail);
    }

    onNotifyMeChange(checked);
    onNotificationEmailChange(emailList.join(', '));
  };

  /**
   * Handle notify others checkbox change
   * Clears other emails when unchecked (keeps user email if notifyMe is checked)
   */
  const handleNotifyOthersChange = (e) => {
    const checked = e.target.checked;
    const trimmedUserEmail = userEmail?.trim() || '';

    onNotifyOthersChange(checked);

    if (!checked) {
      // When unchecking, keep only user email if notifyMe is checked
      if (notifyMe && trimmedUserEmail) {
        onNotificationEmailChange(trimmedUserEmail);
      } else {
        onNotificationEmailChange('');
      }
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="headline-smaller"
        sx={{ mb: 2, fontWeight: '700 !important', display: 'block' }}
      >
        Test report notification
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={notifyMe}
            onChange={handleNotifyMeChange}
            sx={{
              padding: 1,
              '& .MuiCheckboxIconWeave': {
                height: 20,
                width: 20,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ cursor: 'pointer' }}
            onClick={() =>
              handleNotifyMeChange({ target: { checked: !notifyMe } })
            }
          >
            Notify me
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={notifyOthers}
            onChange={handleNotifyOthersChange}
            sx={{
              padding: 1,
              '& .MuiCheckboxIconWeave': {
                height: 20,
                width: 20,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ cursor: 'pointer' }}
            onClick={() =>
              handleNotifyOthersChange({ target: { checked: !notifyOthers } })
            }
          >
            Notify others
          </Typography>
        </Box>
        {showJiraSection && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
              JIRA
            </Typography>
            {onJiraXrayChange && (
              <>
                <Switch
                  checked={updateJiraXray}
                  onChange={(e) => onJiraXrayChange(e.target.checked)}
                />
                <Typography variant="caption">
                  Update Xray with status
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Notification Email Field (shown when notifyOthers is checked) */}
      {notifyOthers && renderEmailField && (
        <TextField
          fullWidth
          variant="outlined"
          value={notificationEmail || ''}
          onChange={(e) => onNotificationEmailChange(e.target.value)}
          placeholder="Enter email addresses"
          size="small"
          helperText="Enter email addresses, separated by commas (e.g., example1@mail.com, example2@mail.com)."
          sx={{
            mt: 2,
            '& .MuiInputBase-root': {
              height: 40,
            },
          }}
        />
      )}
    </Box>
  );
}

export default NotificationSection;
